import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface FilterConfig {
  [key: string]: any;
}

export interface SavedFilter {
  id: string;
  name: string;
  filters: FilterConfig;
  createdAt: Date;
  isDefault?: boolean;
}

const STORAGE_PREFIX = 'seekers_filters_';

export function usePersistentFilters<T extends FilterConfig>(
  componentId: string,
  defaultFilters: T
) {
  const { user } = useAuth();
  const storageKey = `${STORAGE_PREFIX}${componentId}_${user?.id || 'anonymous'}`;

  // Cargar filtros guardados al inicio
  const loadSavedFilters = useCallback((): SavedFilter[] => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map((f: any) => ({
          ...f,
          createdAt: new Date(f.createdAt),
        }));
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error al cargar filtros guardados:', error);
      }
    }
    return [];
  }, [storageKey]);

  // Cargar filtros activos del último uso
  const loadActiveFilters = useCallback((): T => {
    try {
      const activeKey = `${storageKey}_active`;
      const saved = localStorage.getItem(activeKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error al cargar filtros activos:', error);
      }
    }
    return defaultFilters;
  }, [storageKey, defaultFilters]);

  // Guardar filtros activos
  const saveActiveFilters = useCallback((filters: T) => {
    try {
      const activeKey = `${storageKey}_active`;
      localStorage.setItem(activeKey, JSON.stringify(filters));
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error al guardar filtros activos:', error);
      }
    }
  }, [storageKey]);

  // Guardar un nuevo filtro
  const saveFilter = useCallback((name: string, filters: T, isDefault = false) => {
    try {
      const saved = loadSavedFilters();
      const newFilter: SavedFilter = {
        id: Date.now().toString(),
        name,
        filters,
        createdAt: new Date(),
        isDefault,
      };

      // Si es default, quitar default de otros
      if (isDefault) {
        saved.forEach(f => f.isDefault = false);
      }

      const updated = [...saved, newFilter];
      localStorage.setItem(storageKey, JSON.stringify(updated));
      return newFilter;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error al guardar filtro:', error);
      }
      throw error;
    }
  }, [storageKey, loadSavedFilters]);

  // Eliminar un filtro guardado
  const deleteFilter = useCallback((filterId: string) => {
    try {
      const saved = loadSavedFilters();
      const updated = saved.filter(f => f.id !== filterId);
      localStorage.setItem(storageKey, JSON.stringify(updated));
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error al eliminar filtro:', error);
      }
      throw error;
    }
  }, [storageKey, loadSavedFilters]);

  // Aplicar un filtro guardado
  const applyFilter = useCallback((filter: SavedFilter) => {
    saveActiveFilters(filter.filters as T);
    return filter.filters as T;
  }, [saveActiveFilters]);

  // Limpiar todos los filtros
  const clearFilters = useCallback(() => {
    saveActiveFilters(defaultFilters);
  }, [saveActiveFilters, defaultFilters]);

  // Exportar filtros
  const exportFilters = useCallback((): string => {
    try {
      const saved = loadSavedFilters();
      return JSON.stringify(saved, null, 2);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error al exportar filtros:', error);
      }
      return '[]';
    }
  }, [loadSavedFilters]);

  // Importar filtros
  const importFilters = useCallback((jsonString: string) => {
    try {
      const imported = JSON.parse(jsonString);
      if (Array.isArray(imported)) {
        const saved = loadSavedFilters();
        const updated = [...saved, ...imported];
        localStorage.setItem(storageKey, JSON.stringify(updated));
        return true;
      }
      return false;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error al importar filtros:', error);
      }
      return false;
    }
  }, [storageKey, loadSavedFilters]);

  return {
    loadSavedFilters,
    loadActiveFilters,
    saveActiveFilters,
    saveFilter,
    deleteFilter,
    applyFilter,
    clearFilters,
    exportFilters,
    importFilters,
  };
}

